'use client';

import dynamic from 'next/dynamic';
import { Suspense, useMemo, useState, type ReactNode } from 'react';
import { useQueryStates } from 'nuqs';
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
} from '@/components/ui/sidebar';
import type { SettingsTabId, SettingsTabProps } from '@/types/settings';
import { useSlackConfigured } from '@/hooks/useSlackData';
import { SettingsTabPanelSkeleton } from './SettingsTabPanelSkeleton';
import {
  SETTINGS_BASE_TABS,
  SETTINGS_SLACK_TAB,
  resolveSettingsActiveTab,
  settingsTabRelevance,
} from './settingsTabConfig';
import {
  settingsAppnameRaw,
  settingsSearchParamsParsers,
} from './settingsSearchParams';
import { SETTINGS_TAB_PRELOAD } from './settingsTabLoaders';

const tabPanelFallback = () => <SettingsTabPanelSkeleton />;

const DynamicGeneralTab = dynamic(() => import('./tabs/GeneralTab'), {
  loading: tabPanelFallback,
});
const DynamicTeamTab = dynamic(() => import('./tabs/TeamTab'), {
  loading: tabPanelFallback,
});
const DynamicAuthTab = dynamic(() => import('./tabs/AuthTab'), {
  loading: tabPanelFallback,
});
const DynamicCodeManagementTab = dynamic(
  () => import('./tabs/CodeManagement'),
  {
    loading: tabPanelFallback,
  }
);
const DynamicPrSettingsTab = dynamic(() => import('./tabs/PrSettingsTab'), {
  loading: tabPanelFallback,
});
const DynamicApiTab = dynamic(() => import('./tabs/ApiTab'), {
  loading: tabPanelFallback,
});
const DynamicBillingTab = dynamic(
  () => import('../Profile/Billing').then((m) => ({ default: m.Billing })),
  { loading: tabPanelFallback }
);
const DynamicSlackTab = dynamic(() => import('./tabs/SlackTab'), {
  loading: tabPanelFallback,
});

export interface SettingsTabsProps {
  isAdmin: boolean;
  canSeeAdminTabs: boolean;
  orgUuid: string | undefined;
  initialTab: SettingsTabId;
  /** RSC-seeded panel: no extra dynamic() fetch on the landing tab. */
  initialPanel: ReactNode;
  appnameRaw?: string | null;
}

function DynamicTabPanel({
  tab,
  isAdmin,
  canSeeAdminTabs,
  tabProps,
}: {
  tab: SettingsTabId;
  isAdmin: boolean;
  canSeeAdminTabs: boolean;
  tabProps: SettingsTabProps;
}) {
  switch (tab) {
    case 'general':
      return <DynamicGeneralTab {...tabProps} />;
    case 'team':
      return <DynamicTeamTab {...tabProps} />;
    case 'auth':
      return <DynamicAuthTab {...tabProps} />;
    case 'code-management':
      return canSeeAdminTabs ? (
        <DynamicCodeManagementTab {...tabProps} />
      ) : null;
    case 'code-review-settings':
      return isAdmin ? <DynamicPrSettingsTab /> : null;
    case 'api':
      return <DynamicApiTab {...tabProps} />;
    case 'billing':
      return isAdmin ? <DynamicBillingTab /> : null;
    case 'slack-notification':
      return canSeeAdminTabs ? <DynamicSlackTab {...tabProps} /> : null;
    default:
      return null;
  }
}

function SettingsTabsInner({
  isAdmin,
  canSeeAdminTabs,
  orgUuid,
  initialTab,
  initialPanel,
  appnameRaw: initialAppnameRaw,
}: SettingsTabsProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const { data: isSlackConfigured } = useSlackConfigured(orgUuid);
  const [{ tab: tabParam, appname, appName }, setQuery] = useQueryStates(
    settingsSearchParamsParsers
  );

  const appnameRaw = settingsAppnameRaw(appname, appName) ?? initialAppnameRaw;
  const appnameLower = appnameRaw?.toLowerCase();
  const activeTab = resolveSettingsActiveTab(
    tabParam ?? undefined,
    appnameLower,
    isAdmin,
    canSeeAdminTabs
  );
  const tabProps: SettingsTabProps = useMemo(
    () => ({ appname: appnameRaw }),
    [appnameRaw]
  );

  const baseTabs = useMemo(() => {
    const tabs = SETTINGS_BASE_TABS.filter(
      (tab) =>
        (!tab.ownerOnly || isAdmin) && (!tab.adminOnly || canSeeAdminTabs)
    );
    return isSlackConfigured && canSeeAdminTabs
      ? [...tabs, SETTINGS_SLACK_TAB]
      : tabs;
  }, [isAdmin, canSeeAdminTabs, isSlackConfigured]);

  const tabsConfig = useMemo(() => {
    if (!searchQuery.trim()) return baseTabs;
    return [...baseTabs].sort((a, b) => {
      const relevanceA = settingsTabRelevance(a.id, searchQuery);
      const relevanceB = settingsTabRelevance(b.id, searchQuery);
      return relevanceB - relevanceA;
    });
  }, [baseTabs, searchQuery]);

  const handleTabChange = (value: string) => {
    void setQuery({ tab: value, 'page-tab': null });
  };

  const panel =
    activeTab === initialTab ? (
      initialPanel
    ) : (
      <DynamicTabPanel
        tab={activeTab}
        isAdmin={isAdmin}
        canSeeAdminTabs={canSeeAdminTabs}
        tabProps={tabProps}
      />
    );

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-col gap-6 pb-6 pt-4 text-ds-text-default">
      <SidebarProvider className="flex min-h-0 min-w-0 flex-1 items-stretch">
        <Sidebar
          collapsible="none"
          className="mr-7 -ml-4 w-56 shrink-0 border-0 bg-transparent"
        >
          <SidebarHeader className="px-0 pr-4 pb-4">
            <div className="relative">
              <input
                type="search"
                aria-label="Search settings tabs"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-md border border-ds-border-subtlest bg-ds-surface-white py-2 pl-10 pr-4 text-sm text-ds-text-darker focus:border-transparent focus:outline-none focus:ring-2 focus:ring-ds-brand-default"
              />
              <svg
                className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-ds-text-subtler"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </SidebarHeader>
          <SidebarContent className="px-0">
            <SidebarMenu>
              {tabsConfig.map((tab) => {
                const relevance = settingsTabRelevance(tab.id, searchQuery);
                const isRelevant = relevance > 0;
                return (
                  <SidebarMenuItem key={tab.id}>
                    <SidebarMenuButton
                      isActive={activeTab === tab.id}
                      onClick={() => handleTabChange(tab.id)}
                      onMouseEnter={() =>
                        SETTINGS_TAB_PRELOAD[tab.id as SettingsTabId]?.()
                      }
                      className={
                        isRelevant ? 'ring-1 ring-ds-brand-subtler' : ''
                      }
                    >
                      <span className="flex items-center gap-2">
                        {tab.label}
                        {isRelevant ? (
                          <span className="rounded-full bg-ds-brand-subtler px-1.5 py-0.5 text-xs text-ds-brand-subtlest">
                            {relevance}
                          </span>
                        ) : null}
                      </span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarContent>
        </Sidebar>
        <div className="min-h-0 min-w-0 flex-1 overflow-y-auto pr-3">
          {panel}
        </div>
      </SidebarProvider>
    </div>
  );
}

function SettingsTabsSuspenseFallback({
  initialPanel,
}: {
  initialPanel: ReactNode;
}) {
  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-col gap-6 pb-6 pt-4 text-ds-text-default">
      <div className="flex min-h-0 min-w-0 flex-1 items-stretch">
        <div className="mr-7 -ml-4 w-56 shrink-0" aria-hidden />
        <div className="min-h-0 min-w-0 flex-1 overflow-y-auto pr-3">
          {initialPanel}
        </div>
      </div>
    </div>
  );
}

/**
 * Client settings shell: one nuqs reader, RSC-seeded landing tab, dynamic load
 * on switch with hover preload.
 */
export function SettingsTabs(props: SettingsTabsProps) {
  return (
    <Suspense
      fallback={
        <SettingsTabsSuspenseFallback initialPanel={props.initialPanel} />
      }
    >
      <SettingsTabsInner {...props} />
    </Suspense>
  );
}
